import { Injectable, inject } from '@angular/core';
import {
  LicenseUsageTypeReport,
  OrganizationLicenseUsageMonthAvailability,
  OrganizationLicenseUsageReport,
} from '@nimmsta/ng-commons-lib/models';
import { OrganizationService } from '@nimmsta/ng-commons-lib/services/organization';
import { Observable, catchError, forkJoin, map, of } from 'rxjs';

export interface LicenseUsageReportProvider {
  readonly source: string;
  readonly supportedTypes: readonly string[];

  load(
    organizationId: string,
    month: string,
    types: readonly string[],
  ): Observable<OrganizationLicenseUsageReport>;

  loadMonthAvailability(
    organizationId: string,
    types: readonly string[],
  ): Observable<OrganizationLicenseUsageMonthAvailability>;
}

export interface LicenseUsageProviderFailure {
  source: string;
}

export interface AggregatedLicenseUsageReport {
  availableTypes: string[];
  reports: LicenseUsageTypeReport[];
  failures: LicenseUsageProviderFailure[];
}

export interface AggregatedLicenseUsageMonthAvailability {
  availableTypes: string[];
  monthsByType: Record<string, string[]>;
  failures: LicenseUsageProviderFailure[];
}

@Injectable({ providedIn: 'root' })
export class WorkflowLicenseUsageReportProvider
  implements LicenseUsageReportProvider
{
  readonly source = 'workflow';
  readonly supportedTypes = ['ACTIVE_WORKER'];

  private organizationService = inject(OrganizationService);

  load(
    organizationId: string,
    month: string,
    types: readonly string[],
  ): Observable<OrganizationLicenseUsageReport> {
    const selectedTypes = types.filter((type) =>
      this.supportedTypes.includes(type),
    );

    if (types.length > 0 && selectedTypes.length === 0) {
      return of({
        organizationId,
        month,
        availableTypes: [...this.supportedTypes],
        reports: [],
      });
    }

    return this.organizationService.getLicenseUsageReport(
      organizationId,
      month,
      selectedTypes,
    );
  }

  loadMonthAvailability(
    organizationId: string,
    types: readonly string[],
  ): Observable<OrganizationLicenseUsageMonthAvailability> {
    const selectedTypes = types.filter((type) =>
      this.supportedTypes.includes(type),
    );

    if (types.length > 0 && selectedTypes.length === 0) {
      return of({
        organizationId,
        availableTypes: [...this.supportedTypes],
        monthsByType: {},
      });
    }

    return this.organizationService.getLicenseUsageMonthAvailability(
      organizationId,
      selectedTypes,
    );
  }
}

@Injectable({ providedIn: 'root' })
export class LicenseUsageReportAggregationService {
  private workflowProvider = inject(WorkflowLicenseUsageReportProvider);

  private readonly providers: LicenseUsageReportProvider[] = [
    this.workflowProvider,
  ];

  load(
    organizationId: string,
    month: string,
    types: readonly string[],
  ): Observable<AggregatedLicenseUsageReport> {
    return forkJoin(
      this.providers.map((provider) =>
        provider.load(organizationId, month, types).pipe(
          map((response) => ({ provider, response })),
          catchError(() => of({ provider, response: undefined })),
        ),
      ),
    ).pipe(
      map((results) => ({
        availableTypes: this.availableTypes(results),
        reports: results.flatMap((result) => result.response?.reports ?? []),
        failures: results
          .filter((result) => result.response === undefined)
          .map((result) => ({ source: result.provider.source })),
      })),
    );
  }

  loadMonthAvailability(
    organizationId: string,
    types: readonly string[],
  ): Observable<AggregatedLicenseUsageMonthAvailability> {
    return forkJoin(
      this.providers.map((provider) =>
        provider.loadMonthAvailability(organizationId, types).pipe(
          map((response) => ({ provider, response })),
          catchError(() => of({ provider, response: undefined })),
        ),
      ),
    ).pipe(
      map((results) => ({
        availableTypes: this.availableTypes(results),
        monthsByType: this.monthsByType(results),
        failures: results
          .filter((result) => result.response === undefined)
          .map((result) => ({ source: result.provider.source })),
      })),
    );
  }

  private availableTypes(
    results: Array<{
      provider: LicenseUsageReportProvider;
      response:
        | OrganizationLicenseUsageMonthAvailability
        | OrganizationLicenseUsageReport
        | undefined;
    }>,
  ): string[] {
    return [
      ...new Set(
        results.flatMap((result) =>
          result.response?.availableTypes ?? result.provider.supportedTypes,
        ),
      ),
    ].sort();
  }

  private monthsByType(
    results: Array<{
      provider: LicenseUsageReportProvider;
      response: OrganizationLicenseUsageMonthAvailability | undefined;
    }>,
  ): Record<string, string[]> {
    const merged = new Map<string, Set<string>>();
    results.forEach((result) => {
      Object.entries(result.response?.monthsByType ?? {}).forEach(
        ([type, months]) => {
          const values = merged.get(type) ?? new Set<string>();
          months.forEach((month) => values.add(month));
          merged.set(type, values);
        },
      );
    });

    return Object.fromEntries(
      [...merged.entries()].map(([type, months]) => [
        type,
        [...months].sort(),
      ]),
    );
  }
}
